'use client';

import type { ReactNode } from 'react';
import { Checked } from '@assets/icons';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { EXTERNAL_LINKS } from '@lib/constants/apis';
import { cn } from 'cn';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
const consentRowClassName = 'flex items-start gap-3';
const consentContainerClassName = 'border-border flex items-start gap-3 rounded-lg border p-4';
function ConsentLabel({
  asFormLabel = true
}: {
  asFormLabel?: boolean;
}) {
  const content = <>
      I agree to receive recurring text messages from Clai Inc. at the number
      provided, including deal updates, task assignments, and signature
      reminders. Message frequency varies. Message and data rates may apply.
      Reply STOP to opt out, HELP for help. See our{' '}
      <a href={EXTERNAL_LINKS.TERMS} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 underline underline-offset-2 transition-colors" onClick={event => event.stopPropagation()}>
        Terms of Service
      </a>{' '}
      and{' '}
      <a href={EXTERNAL_LINKS.PRIVACY} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-foreground/80 underline underline-offset-2 transition-colors" onClick={event => event.stopPropagation()}>
        Privacy Policy
      </a>
      .
    </>;
  if (asFormLabel) {
    return <FormLabel className="text-muted-foreground cursor-pointer text-sm leading-6 font-normal">
        {content}
      </FormLabel>;
  }
  return <p className="text-muted-foreground text-sm leading-6 font-normal" data-sentry-component="ConsentLabel" data-sentry-source-file="sms-notifications-consent-field.tsx">
      {content}
    </p>;
}
function DisabledConsentCheckboxIndicator({
  checked
}: {
  checked: boolean;
}) {
  return <span aria-hidden data-state={checked ? 'checked' : 'unchecked'} className={cn('ease border-tertiary-300 bg-background mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-[1.5px] p-0 opacity-50 shadow-sm', checked && 'border-primary bg-primary')} data-sentry-component="DisabledConsentCheckboxIndicator" data-sentry-source-file="sms-notifications-consent-field.tsx">
      {checked ? <Checked className="text-primary-foreground h-2.5" /> : null}
    </span>;
}
function DisabledConsentCheckbox({
  checked,
  hint
}: {
  checked: boolean;
  hint: string;
}) {
  return <Popover data-sentry-element="Popover" data-sentry-component="DisabledConsentCheckbox" data-sentry-source-file="sms-notifications-consent-field.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="sms-notifications-consent-field.tsx">
        <Button type="button" variant="unstyled" size="icon-sm" className="mt-0.5 shrink-0" aria-label={hint} data-sentry-element="Button" data-sentry-source-file="sms-notifications-consent-field.tsx">
          <DisabledConsentCheckboxIndicator checked={checked} data-sentry-element="DisabledConsentCheckboxIndicator" data-sentry-source-file="sms-notifications-consent-field.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="max-w-xs text-sm" onOpenAutoFocus={event => event.preventDefault()} data-sentry-element="PopoverContent" data-sentry-source-file="sms-notifications-consent-field.tsx">
        {hint}
      </PopoverContent>
    </Popover>;
}
function ConsentFieldBody({
  asFormLabel,
  checkbox,
  footer
}: {
  asFormLabel: boolean;
  checkbox: ReactNode;
  footer?: ReactNode;
}) {
  return <>
      {checkbox}
      <div className="space-y-1">
        <ConsentLabel asFormLabel={asFormLabel} data-sentry-element="ConsentLabel" data-sentry-source-file="sms-notifications-consent-field.tsx" />
        {footer}
      </div>
    </>;
}
type SmsNotificationsConsentFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>;
  name: TName;
  checked?: never;
  onCheckedChange?: never;
  error?: never;
} | {
  control?: never;
  name?: never;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  error?: string | null;
  disabled?: boolean;
  disabledHint?: string;
  variant?: 'boxed' | 'unboxed';
  className?: string;
};
export function SmsNotificationsConsentField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(props: SmsNotificationsConsentFieldProps<TFieldValues, TName>) {
  if ('control' in props && props.control) {
    const {
      control,
      name
    } = props;
    return <FormField control={control} name={name} render={({
      field
    }) => <FormItem className={consentContainerClassName}>
            <ConsentFieldBody asFormLabel checkbox={<FormControl>
                  <Checkbox checked={field.value} onCheckedChange={checked => field.onChange(checked === true)} aria-label="SMS consent" />
                </FormControl>} footer={<FormMessage />} />
          </FormItem>} />;
  }
  const {
    checked,
    onCheckedChange,
    error,
    disabled = false,
    disabledHint,
    variant = 'boxed',
    className
  } = props;
  const wrapperClassName = variant === 'unboxed' ? consentRowClassName : consentContainerClassName;
  return <div className={cn(wrapperClassName, className)} data-sentry-component="SmsNotificationsConsentField" data-sentry-source-file="sms-notifications-consent-field.tsx">
      <ConsentFieldBody asFormLabel={false} checkbox={disabled && disabledHint ? <DisabledConsentCheckbox checked={checked} hint={disabledHint} /> : <Checkbox checked={checked} disabled={disabled} onCheckedChange={value => onCheckedChange(value === true)} aria-label="SMS consent" className="mt-0.5" />} footer={error ? <p className="text-destructive text-sm">{error}</p> : null} data-sentry-element="ConsentFieldBody" data-sentry-source-file="sms-notifications-consent-field.tsx" />
    </div>;
}